import { useCallback, useMemo } from "react";
import { useCartStore, CartItem } from "../store/cart-store";
import { AddToCartRequest } from "@/api/models/api-types";
import { useToast } from "@/features/toast";
import useAuthStore from "@/store/use-auth-store";
import { formatPrice as formatPriceUtil } from "@/features/orders/utils/format-price";

// 🛒 Cart Hook Interface
export interface UseCartReturn {
  // 📊 State
  items: CartItem[];
  totalItems: number;
  totalPrice: number;
  subtotal: number;
  tax: number;
  shipping: number;
  cashback: number;
  isLoading: boolean;
  error: string | null;
  isEmpty: boolean;

  // NEW: Vendor Groups (for checkout)
  vendorGroups: any[];

  // ⚡ Actions
  addToCart: (request: AddToCartRequest) => Promise<void>;
  removeFromCart: (cartId: string) => Promise<void>;
  updateQuantity: (cartId: string, quantity: number) => Promise<void>;
  clearCart: () => Promise<void>;

  // 🛡️ Utilities
  getCartItem: (cartId: string) => any;
  getCartItemByProductId: (productId: string) => CartItem | undefined;
  isInCart: (productId: string) => boolean;
  getItemQuantity: (productId: string) => number;

  // 💰 Formatters
  formatPrice: (price: number) => string;
  getFreeShippingRemaining: () => number;
  isFreeShippingEligible: () => boolean;

  // 🌐 Backend Actions
  loadCart: (offerId?: string) => Promise<void>;
}

// 🎯 Main Cart Hook (Updated for Hierarchical Structure)
export const useCart = (): UseCartReturn => {
  // Store'dan hierarchical data al
  const {
    merchants,
    globalCalculation,
    isLoading,
    error,
    addToCart: storeAddToCart,
    removeFromCart: storeRemoveFromCart,
    updateQuantity: storeUpdateQuantity,
    clearCart: storeClearCart,
    getCartItem: storeGetCartItem,
    isInCart: storeIsInCart,
    loadCart: storeLoadCart,
  } = useCartStore();

  const { success, error: showError, info } = useToast();
  const userCurrency = useAuthStore((state) => state.user?.currency) || "TRY";

  // 📊 Computed Values (Backend calculation'larından türetilmiş)
  const totalItems = useMemo(() => {
    return merchants.reduce(
      (sum, merchant) =>
        sum +
        merchant.items.reduce((itemSum, item) => itemSum + item.quantity, 0),
      0,
    );
  }, [merchants]);

  const subtotal = globalCalculation?.subTotal || 0;
  const shipping = globalCalculation?.cargo || 0;
  const totalPrice = globalCalculation?.total || 0;
  const cashback = globalCalculation?.cashback || 0;

  // Tax hesaplama (display için - KDV dahil fiyatlardan)
  const tax = useMemo(() => {
    return subtotal - subtotal / 1.18;
  }, [subtotal]);

  // 🔄 Backward Compatibility: Flat items array
  const items = useMemo((): CartItem[] => {
    return merchants.flatMap((merchant) =>
      merchant.items.map((item) => {
        // Primary image'i bul (varsa)
        const primaryImage = item.offer.images?.find((img) => img.isPrimary);
        const firstImage = item.offer.images?.[0];
        const imageUrl = primaryImage?.imageUrl || firstImage?.imageUrl || "";

        // Price calculation
        const finalPrice = item.offer.campaignPrice || item.offer.salePrice;
        const originalPrice = item.offer.salePrice; // Liste fiyatı

        return {
          _id: item._id,
          productId: item.offer.productId,
          _product: item.offer._product ?? item.offer.productId,
          title: item.offer.name,
          description: item.offer.productName,
          type: "product",
          file: imageUrl, // Backend'den gelecek images array'inden primary image
          price: finalPrice,
          quantity: item.quantity,
          userId: "",
          isDeleted: false,
          isSelected: item.isSelected,
          createdAt: "",
          updatedAt: "",
          vendor: {
            id: merchant.merchantId,
            name: merchant.merchantName,
            logo: item.offer.sellerLogoUrl || "",
            slug: merchant.merchantSeoName,
          },
          metadata: {
            // Variant bilgileri (YENİ!)
            axis1Name: item.offer.axis1Name,
            axis2Name: item.offer.axis2Name,
            a1Value: item.offer.a1Value,
            a2Value: item.offer.a2Value,

            // Price bilgileri (YENİ!)
            originalPrice: originalPrice,
            discountPct: item.offer.campaignPrice
              ? Math.round(
                  ((originalPrice - item.offer.campaignPrice) / originalPrice) *
                    100,
                )
              : 0,

            // Stock bilgileri (YENİ!)
            stockCount: item.offer.stockCount,
            inStock: item.offer.inStock,

            // Brand/Seller bilgileri
            brand: item.offer.brandName,
            seller: item.offer.sellerName,

            // Legacy fields (eski metadata uyumluluğu için)
            size: item.offer.a1Value || item.offer.a2Value,
            color: item.offer.a2Value,
          },
          priceFormatted: formatPriceUtil(finalPrice, userCurrency),
          totalPrice: finalPrice * item.quantity,
        };
      }),
    );
  }, [merchants]);

  // 🏪 Vendor Groups (checkout için)
  const vendorGroups = useMemo(() => {
    return merchants.map((merchant) => ({
      vendorId: merchant.merchantId,
      vendorName: merchant.merchantName,
      vendorLogo: merchant.items[0]?.offer.sellerLogoUrl,
      items: merchant.items,
      itemCount: merchant.items.length,
      subtotal: merchant.calculation.subTotal,
      shipping: merchant.calculation.cargo,
      total: merchant.calculation.total,
    }));
  }, [merchants]);

  // 🎯 Enhanced Add to Cart with Toast
  const addToCart = useCallback(
    async (request: AddToCartRequest) => {
      try {
        await storeAddToCart(request);
        success(`Ürün sepete eklendi`, {
          title: "Sepete Eklendi",
          duration: 3000,
          position: "bottom-right",
        });
      } catch (addError: any) {
        console.error("Add to cart error:", addError);
        showError(
          addError?.response?.data?.message ||
            addError?.message ||
            "Ürün sepete eklenirken bir hata oluştu.",
          {
          title: "Hata",
          position: "bottom-right",
          },
        );
      }
    },
    [storeAddToCart, success, showError],
  );

  // 🗑️ Enhanced Remove from Cart with Toast
  const removeFromCart = useCallback(
    async (cartId: string) => {
      try {
        // Find item from hierarchical structure
        const item = storeGetCartItem(cartId);
        await storeRemoveFromCart(cartId);

        if (item) {
          info(`${item.offer.name} sepetten kaldırıldı`, {
            title: "Sepetten Kaldırıldı",
            duration: 3000,
            position: "bottom-right",
          });
        }
      } catch (removeError: any) {
        console.error("Remove from cart error:", removeError);
        showError("Ürün sepetten kaldırılırken bir hata oluştu.", {
          title: "Hata",
          position: "bottom-right",
        });
      }
    },
    [storeRemoveFromCart, storeGetCartItem, info, showError],
  );

  // 🔢 Enhanced Update Quantity with Toast
  const updateQuantity = useCallback(
    async (cartId: string, quantity: number) => {
      try {
        // Find item from hierarchical structure
        const item = storeGetCartItem(cartId);
        await storeUpdateQuantity(cartId, quantity);

        if (item) {
          info(`${item.offer.name} miktarı ${quantity} olarak güncellendi`, {
            title: "Miktar Güncellendi",
            duration: 2000,
            position: "bottom-right",
          });
        }
      } catch (updateError: any) {
        console.error("Update quantity error:", updateError);
        showError("Ürün miktarı güncellenirken bir hata oluştu.", {
          title: "Hata",
          position: "bottom-right",
        });
      }
    },
    [storeUpdateQuantity, storeGetCartItem, info, showError],
  );

  // 🧹 Enhanced Clear Cart with Toast
  const clearCart = useCallback(async () => {
    try {
      const itemCount = totalItems;
      await storeClearCart();

      info(`${itemCount} ürün sepetten temizlendi`, {
        title: "Sepet Temizlendi",
        duration: 3000,
        position: "bottom-right",
      });
    } catch (clearError: any) {
      console.error("Clear cart error:", clearError);
      showError("Sepet temizlenirken bir hata oluştu.", {
        title: "Hata",
        position: "bottom-right",
      });
    }
  }, [storeClearCart, totalItems, info, showError]);

  // 🛡️ Utility Functions
  const getCartItem = useCallback(
    (cartId: string) => {
      return storeGetCartItem(cartId);
    },
    [storeGetCartItem],
  );

  const getCartItemByProductId = useCallback(
    (productId: string) => {
      // Legacy compatibility: search by productId in flat items array
      return items.find((item) => item.productId === productId);
    },
    [items],
  );

  const isInCart = useCallback(
    (productId: string) => {
      return storeIsInCart(productId);
    },
    [storeIsInCart],
  );

  const getItemQuantity = useCallback(
    (productId: string) => {
      const item = getCartItemByProductId(productId);
      return item?.quantity || 0;
    },
    [getCartItemByProductId],
  );

  // 💰 Formatting and Calculation Utilities
  const formatPrice = useCallback(
    (price: number) => formatPriceUtil(price, userCurrency),
    [userCurrency],
  );

  const getFreeShippingRemaining = useCallback(() => {
    const freeShippingThreshold = 500;
    const remaining = freeShippingThreshold - subtotal;
    return remaining > 0 ? remaining : 0;
  }, [subtotal]);

  const isFreeShippingEligible = useCallback(() => {
    return subtotal >= 500;
  }, [subtotal]);

  // 🌐 Backend Actions
  const loadCart = useCallback(
    async (offerId?: string) => {
      try {
        await storeLoadCart(offerId);
      } catch (loadError: any) {
        console.error("Cart load error:", loadError);
        showError("Sepet yüklenirken bir hata oluştu", {
          title: "Yükleme Hatası",
          position: "bottom-right",
        });
      }
    },
    [storeLoadCart, showError],
  );

  // 📊 Derived State
  const isEmpty = merchants.length === 0;

  return {
    // State (Backward compatibility)
    items,
    totalItems,
    totalPrice,
    subtotal,
    tax,
    shipping,
    cashback,
    isLoading,
    error,
    isEmpty,

    // NEW: Vendor groups (for checkout)
    vendorGroups,

    // Actions
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,

    // Utilities
    getCartItem,
    getCartItemByProductId,
    isInCart,
    getItemQuantity,

    // Formatters
    formatPrice,
    getFreeShippingRemaining,
    isFreeShippingEligible,

    // Backend Actions
    loadCart,
  };
};
